/**
 * フロント側の日付ユーティリティ（SPEC §2.3）。
 *
 * 暦計算は `shared/date.ts`（Date 非依存の純関数）に委譲する。
 * このファイルで `Date` を使うのは「端末ローカルの今日が何日か」を得る 1 箇所だけで、
 * 得られた `YYYY-MM-DD` は以後ただの文字列として扱う。
 */
export {
  addDays,
  daysInclusive,
  diffDays,
  isBusinessDate,
  lastMonths,
  monthOf,
  type BusinessDate,
  type MonthKey,
} from '../../shared/date';

const pad2 = (n: number): string => String(n).padStart(2, '0');

/** 端末ローカルの今日を `YYYY-MM-DD` で返す */
export function today(): string {
  const d = new Date();
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

/** 端末ローカルの今月を `YYYY-MM` で返す */
export const thisMonth = (): string => today().slice(0, 7);

/* ------------------------------------------------------------------ */
/* 表示用フォーマット                                                    */
/* ------------------------------------------------------------------ */

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

/** `2026-08-08` → `8/8` */
export function formatMd(d: string): string {
  if (!DATE_RE.test(d)) return d;
  return `${Number(d.slice(5, 7))}/${Number(d.slice(8, 10))}`;
}

/** `2026-08-08` → `2026/8/8` */
export function formatYmd(d: string): string {
  if (!DATE_RE.test(d)) return d;
  return `${d.slice(0, 4)}/${Number(d.slice(5, 7))}/${Number(d.slice(8, 10))}`;
}

/** `2026-08` → `2026年8月` */
export function formatMonth(ym: string): string {
  if (!/^\d{4}-\d{2}$/.test(ym)) return ym;
  return `${ym.slice(0, 4)}年${Number(ym.slice(5, 7))}月`;
}

/** `2026-08` → `8月`（グラフの軸ラベル用） */
export function formatMonthShort(ym: string): string {
  if (!/^\d{4}-\d{2}$/.test(ym)) return ym;
  return `${Number(ym.slice(5, 7))}月`;
}

/** 円の表示。小数は四捨五入する（SPEC §4.3） */
export const yen = (v: number): string => `${Math.round(v).toLocaleString('ja-JP')}円`;

/** 円の表示（記号のみ・グラフの軸用） */
export const yenShort = (v: number): string => `¥${Math.round(v).toLocaleString('ja-JP')}`;
